import { sumDeliveryFees } from '../../services/deliveryFees';

export const STORAGE_KEYS = Object.freeze({
  tab: 'merchant_tab',
  filters: 'merchant_filters',
  period: 'merchant_bilan_period',
  customRange: 'merchant_bilan_custom_range',
});

export function readJSON(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return parsed ?? fallback;
  } catch {
    return fallback;
  }
}

export function toTimestamp(value) {
  const ts = value ? new Date(value).getTime() : 0;
  return Number.isFinite(ts) ? ts : 0;
}

export function sortByCreatedAtDesc(list) {
  return [...list].sort((left, right) => toTimestamp(right.created_at) - toTimestamp(left.created_at));
}

export function upsertById(list, row) {
  const index = list.findIndex((item) => item.id === row.id);
  if (index === -1) return sortByCreatedAtDesc([row, ...list]);
  const next = [...list];
  next[index] = { ...next[index], ...row };
  return sortByCreatedAtDesc(next);
}

export function removeById(list, id) {
  return list.filter((item) => item.id !== id);
}

export function isActiveMerchantDelivery(row) {
  return Boolean(row) && !row.expedition && (row.status !== 'delivered' || row.versement_id == null);
}

export function getRefDate(delivery) {
  if (delivery.status === 'delivered') {
    const raw = delivery.delivered_at || delivery.created_at;
    const dt = new Date(raw);
    return Number.isFinite(dt.getTime()) ? dt : new Date(delivery.created_at);
  }
  return new Date(delivery.created_at);
}

export function filterMerchantDeliveriesByPeriode(list, period, customRange = { from: '', to: '' }) {
  const now = new Date();
  return list.filter((delivery) => {
    const date = getRefDate(delivery);
    if (period === 'jour') return date.toDateString() === now.toDateString();
    if (period === 'semaine') {
      const start = new Date(now);
      start.setDate(now.getDate() - ((now.getDay() + 6) % 7));
      start.setHours(0, 0, 0, 0);
      return date >= start;
    }
    if (period === 'mois') {
      return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
    }
    if (period === 'custom' && customRange.from && customRange.to) {
      const from = new Date(customRange.from);
      const to = new Date(`${customRange.to}T23:59:59`);
      return date >= from && date <= to;
    }
    return true;
  });
}

export function parseMoney(value) {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (typeof value !== 'string') return 0;
  const cleaned = value.replace(/\s/g, '').replace(',', '.');
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function formatPhoneDisplay(value) {
  if (!value) return '—';
  const digits = String(value).replace(/\D/g, '');
  let local = digits;
  if (digits.length === 12 && digits.startsWith('221')) local = digits.slice(3);
  if (digits.length === 14 && digits.startsWith('00221')) local = digits.slice(5);
  if (local.length === 9) return `+221 ${local.slice(0, 2)} ${local.slice(2, 5)} ${local.slice(5, 7)} ${local.slice(7, 9)}`;
  if (digits.length > 0) return `+${digits}`;
  return String(value);
}

export function toDialPhone(value) {
  if (!value) return null;
  const digits = String(value).replace(/\D/g, '');
  if (digits.length === 12 && digits.startsWith('221')) return `+${digits}`;
  if (digits.length === 14 && digits.startsWith('00221')) return `+${digits.slice(2)}`;
  if (digits.length === 9) return `+221${digits}`;
  return digits ? `+${digits}` : null;
}

/**
 * @param {Array}  list       livraisons de la boutique
 * @param {object} storeFees  { fee_dakar, fee_expedition } du marchand.
 *   Sans lui, le calcul retombait sur la constante globale et affichait au
 *   marchand des frais différents de ceux qu'on lui facture réellement.
 */
export function calcMerchantBilan(list, storeFees = null) {
  const delivered = list.filter((delivery) => delivery.status === 'delivered');
  const ca = delivered.reduce((sum, delivery) => sum + parseMoney(delivery.amount), 0);
  const frais = sumDeliveryFees(delivered, storeFees);
  return {
    ca,
    frais,
    net: ca - frais,
    nbLivrees: delivered.length,
    nbTotal: list.length,
    nbAttente: list.filter((delivery) => delivery.status === 'pending').length,
    nbTransit: list.filter((delivery) => delivery.status === 'transit').length,
    nbEchec: list.filter((delivery) => delivery.status === 'failed').length,
  };
}
